import type { SvgPathSubpath } from '../types/svg-path-subpath'
import { groupSubpaths, parseSegments } from './svg-path-parse'

/** Round-trip-safe formatting: drop the noise a float introduces without moving the point. */
function fmt(n: number): string {
  return Number.isInteger(n) ? String(n) : String(Number(n.toFixed(6)))
}

const EPSILON = 1e-9

function reverseSubpath(subpath: SvgPathSubpath): string {
  const { startX, startY, segments, closed } = subpath
  // Point each segment departs FROM, so a reversed segment knows its own endpoint.
  const origins: Array<{ x: number; y: number }> = []
  let px = startX
  let py = startY

  for (const segment of segments) {
    origins.push({ x: px, y: py })
    px = segment.x
    py = segment.y
  }

  const endX = px
  const endY = py
  const parts: string[] = []

  if (closed) {
    // A closed subpath also contains the implicit line from its last point back to its first, so
    // the reversed traversal leaves the START point along that line before undoing any segment.
    // Omitted when the path already returned to its origin, where that line has zero length —
    // emitting it would add a degenerate segment that shifts `getPointAtLength` bookkeeping.
    parts.push(`M ${fmt(startX)} ${fmt(startY)}`)
    if (Math.abs(endX - startX) > EPSILON || Math.abs(endY - startY) > EPSILON) {
      parts.push(`L ${fmt(endX)} ${fmt(endY)}`)
    }
  } else {
    parts.push(`M ${fmt(endX)} ${fmt(endY)}`)
  }

  for (let i = segments.length - 1; i >= 0; i--) {
    const segment = segments[i]!
    const origin = origins[i]!

    switch (segment.type) {
      case 'L':
        parts.push(`L ${fmt(origin.x)} ${fmt(origin.y)}`)
        break
      case 'C':
        // Control points swap with the direction of travel.
        parts.push(
          `C ${fmt(segment.x2)} ${fmt(segment.y2)} ${fmt(segment.x1)} ${fmt(segment.y1)} ${fmt(origin.x)} ${fmt(origin.y)}`
        )
        break
      case 'Q':
        // A quadratic has one control point, which is direction-independent.
        parts.push(`Q ${fmt(segment.x1)} ${fmt(segment.y1)} ${fmt(origin.x)} ${fmt(origin.y)}`)
        break
      case 'A':
        // Radii, rotation and the large-arc choice all describe the ellipse, not the direction —
        // only the sweep flag says which of the two arcs between the endpoints is taken, so it is
        // the single field that flips.
        parts.push(
          `A ${fmt(segment.rx)} ${fmt(segment.ry)} ${fmt(segment.rotation)} ${segment.largeArc} ${segment.sweep ? 0 : 1} ${fmt(origin.x)} ${fmt(origin.y)}`
        )
        break
    }
  }

  if (closed) parts.push('Z')

  return parts.join(' ')
}

/**
 * Reverse `d`'s direction of travel, or `null` when it cannot be parsed.
 *
 * Subpaths are emitted in reverse order as well as individually reversed, so the whole traversal —
 * which is what `<textPath>` follows — runs backwards end to end.
 */
export function reverseSvgPath(d: string): string | null {
  if (!d || !d.trim()) return null

  const segments = parseSegments(d)

  if (!segments) return null

  const subpaths = groupSubpaths(segments)

  if (subpaths.length === 0) return null

  return subpaths.map(reverseSubpath).reverse().join(' ')
}

/**
 * Whether `d` closes back onto its own starting point — i.e. traversal is a loop.
 *
 * Geometric rather than syntactic: a shape that returns to its origin with an explicit segment is
 * a loop whether or not it also writes `Z`, and that is the property `reversedStartOffset` needs.
 */
export function isClosedSvgPath(d: string): boolean {
  const segments = parseSegments(d)

  if (!segments) return false

  const subpaths = groupSubpaths(segments)
  const first = subpaths[0]
  const last = subpaths[subpaths.length - 1]

  if (!first || !last) return false

  const lastSegment = last.segments[last.segments.length - 1]

  if (!lastSegment) return false
  if (last.closed) return true

  return (
    Math.abs(lastSegment.x - first.startX) <= EPSILON &&
    Math.abs(lastSegment.y - first.startY) <= EPSILON
  )
}

/**
 * The `startOffset` that keeps text anchored to the same point on the shape after reversal.
 *
 * The start handle is a position on the object's outline, and AC-7/AC-8 both flow "from the start
 * position" — so flipping direction must pivot the text about that point, not move it. Distance `t`
 * from the original start is `totalLength - t` from the reversed one.
 *
 * `closed` exists because on a loop the two ends are the SAME point, and `<textPath>` does not
 * wrap: a start distance of 0 maps to `pathLength`, which sits exactly at the path's end with no
 * room left, and the text vanished entirely (measured in Chrome — every glyph collapsed to the
 * origin). On a loop that offset is equivalent to 0, so it wraps. An open path genuinely has
 * nothing before its start point, so it is left to clip rather than being silently moved somewhere
 * the user did not put it.
 */
export function reversedStartOffset(
  startDistance: number,
  pathLength: number,
  closed = false
): number {
  if (!Number.isFinite(startDistance) || !Number.isFinite(pathLength) || pathLength <= 0) return 0
  const clamped = Math.min(Math.max(startDistance, 0), pathLength)
  const mapped = pathLength - clamped

  return closed && mapped >= pathLength ? 0 : mapped
}
